import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { FileText, GraduationCap, FolderOpen, Scale, CheckCircle, Mail, Phone, ArrowRight } from "lucide-react";

const NOVITA = [
  "maggiore trasparenza nelle informazioni sulle retribuzioni durante le procedure di selezione del personale",
  "nuovi diritti di informazione riconosciuti ai lavoratori in merito ai livelli retributivi e ai criteri utilizzati per determinarli",
  "obblighi di trasparenza sulle politiche retributive adottate dalle imprese",
  "strumenti per individuare e correggere eventuali differenze retributive non giustificate",
  "specifici obblighi di monitoraggio e rendicontazione per le imprese nei casi previsti dalla normativa",
  "rafforzamento delle tutele a favore dei lavoratori in caso di discriminazione retributiva",
  "maggiore attenzione alla definizione di sistemi retributivi fondati su criteri oggettivi, trasparenti e neutrali rispetto al genere",
];

const TEMI_WEBINAR = [
  "il quadro normativo nazionale",
  "i principali obblighi introdotti dal decreto",
  "gli impatti organizzativi per le imprese",
  "le ricadute sulle procedure di selezione e sulle politiche retributive",
  "gli adempimenti previsti dalla nuova disciplina",
  "le prospettive applicative",
];

const DOCUMENTI = [
  { icon: FileText, label: "Nota tecnica di interpretazione", url: "https://www.confcommercioroma.it/wp-content/uploads/2026/07/Nota-Tecnica-D.-Lgs.-n.-96-2026-1.pdf" },
  { icon: GraduationCap, label: "Slide del webinar", url: "https://www.confcommercioroma.it/wp-content/uploads/2026/07/Slides_webinar_DL_pay_trasparency_9_luglio_2026_final.pdf" },
  { icon: FolderOpen, label: "Documentazione illustrata durante l'incontro", url: "https://www.confcommercioroma.it/wp-content/uploads/2026/07/Casi_pratici_e_schema_d.lgs_._96_2026.pdf" },
  { icon: Scale, label: "Testo del Decreto di recepimento della Direttiva (UE) 2023/970", url: "https://www.confcommercioroma.it/wp-content/uploads/2026/07/D.-lgs.-n.-96-2026-1.pdf" },
];

export default function TrasparenzaSalarialePage() {
  return (
    <main id="main-content">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[320px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?w=1920&q=60&auto=format"
          alt="Trasparenza salariale"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Trasparenza salariale</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--confcom-blue)] mb-3">
            Decreto di recepimento della Direttiva (UE) 2023/970 — Normativa e materiali di approfondimento
          </p>
          <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
            La trasparenza salariale introduce nuovi obblighi per le imprese e maggiori tutele per i lavoratori. Confcommercio Roma ti supporta nella comprensione delle nuove regole e nell'adeguamento di processi, adempimenti e politiche retributive.
          </p>

          <div className="p-6 bg-[#EBF5FF] rounded-xl mb-6">
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
              Il <strong className="text-[var(--confcom-text-primary)]">1° giugno 2026</strong> è stato pubblicato in Gazzetta Ufficiale il <strong className="text-[var(--confcom-text-primary)]">Decreto Legislativo 7 maggio 2026, n. 96</strong> con cui è stata recepita nell'ordinamento nazionale la <strong className="text-[var(--confcom-text-primary)]">Direttiva UE 970/2023</strong>, con l'obiettivo di rafforzare l'effettiva applicazione del principio di parità retributiva attraverso specifici strumenti di trasparenza e relativi meccanismi di tutela. Le disposizioni sono entrate in vigore il <strong className="text-[var(--confcom-text-primary)]">7 giugno 2026</strong>.
            </p>
          </div>

          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            La nuova disciplina rappresenta un passaggio significativo nell'evoluzione della normativa sul lavoro, prevedendo obblighi di trasparenza, nuovi diritti di informazione per lavoratori e candidati e specifiche misure finalizzate a prevenire e contrastare le discriminazioni retributive.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Per accompagnare le imprese associate nella comprensione delle novità normative, <strong className="text-[var(--confcom-text-primary)]">Confcommercio Roma</strong> mette a disposizione una raccolta di documenti, materiali di approfondimento e strumenti operativi elaborati dai propri uffici.
          </p>
        </div>
      </section>

      {/* Le principali novità */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">Le principali novità</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">
            Il decreto introduce una serie di disposizioni che rafforzano il principio della trasparenza salariale e della parità di trattamento, incidendo su diverse fasi del rapporto di lavoro. Tra le principali novità si segnalano:
          </p>
          <ul className="space-y-3">
            {NOVITA.map((n) => (
              <li key={n} className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                <span>{n}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-[var(--confcom-text-secondary)] mt-8">
            L'effettiva applicazione delle nuove disposizioni richiederà alle imprese un'attenta valutazione dei propri processi di gestione delle risorse umane e delle politiche retributive.
          </p>
        </div>
      </section>

      {/* A chi si applica */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">A chi si applica</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            La disciplina interessa progressivamente i datori di lavoro pubblici e privati, introducendo obblighi differenziati in relazione alla tipologia di adempimento prevista e alle caratteristiche dell'impresa.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
            Alcuni obblighi riguardano la generalità dei datori di lavoro, mentre ulteriori adempimenti trovano applicazione nei confronti delle imprese che rientrano nelle soglie dimensionali individuate dalla normativa. Per comprendere nel dettaglio gli obblighi applicabili alla propria realtà aziendale si invita a consultare la nota tecnica predisposta dagli uffici di Confcommercio Roma.
          </p>
          <a
            href={DOCUMENTI[0].url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trasparenza-salariale-nota-tecnica"
            className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-6 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
          >
            <FileText size={16} aria-hidden="true" /> Nota tecnica di interpretazione
          </a>
        </div>
      </section>

      {/* Webinar */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">Webinar del 9 luglio 2026</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            Per illustrare i contenuti del decreto e analizzarne gli effetti operativi, Confcommercio ha tenuto in data 9 luglio un webinar dedicato, dal titolo <em>"Trasparenza retributiva e parità salariale: cosa cambia per le imprese"</em>, al quale hanno preso parte anche le imprese associate.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">Nel corso dell'incontro sono state approfondite molteplici tematiche, quali:</p>
          <ul className="space-y-3 mb-8">
            {TEMI_WEBINAR.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
            Sono disponibili in questa pagina le slide utilizzate dai relatori e gli altri materiali illustrati durante il webinar.
          </p>
          <div className="flex flex-wrap gap-3">
            {DOCUMENTI.slice(1).map((d) => (
              <a
                key={d.label}
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`trasparenza-salariale-doc-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
                className="inline-flex items-center gap-2 bg-white border border-[var(--confcom-border)] text-[var(--confcom-text-primary)] px-5 py-2.5 rounded-md font-medium hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)] transition-colors text-sm"
              >
                <d.icon size={16} className="text-[var(--confcom-blue)]" aria-hidden="true" /> {d.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?w=1920&q=60&auto=format" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.92)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="trasparenza-salariale-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:sindacale@confcommercioroma.it" className="hover:text-white transition-colors">sindacale@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437221" className="hover:text-white transition-colors">Tel: 06 68437 221-290</a>
              </div>
            </div>
          </div>

          <Link
            to="/chi-siamo/struttura-tecnica#ufficio-lavoro-e-welfare"
            data-testid="trasparenza-salariale-scopri-team"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:bg-white/90 transition-colors text-sm mt-8"
          >
            Scopri il Team <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}
